import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useSuggestionsStore } from '@/store/suggestions.store'
import { useAuthStore } from '@/store/auth.store'
import { accountsApi } from '@/api'
import { accountPath } from '@/lib/accountPath'
import { Avatar } from './Avatar'
import { BadgeList } from './BadgeList'
import type { AccountWithStats } from '@olimhub/shared'

interface SuggestedAccountsProps {
  mode: 'list' | 'strip'
  heading?: string
}

function SuggestedRow({ account, onFollowed }: { account: AccountWithStats; onFollowed: (id: string, username: string) => void }) {
  const { isAuthenticated } = useAuthStore()
  const navigate = useNavigate()
  const [localFollowing, setLocalFollowing] = useState(account.isFollowing ?? false)

  async function handleFollow() {
    if (!isAuthenticated) { navigate('/login'); return }
    setLocalFollowing(true)
    try {
      await accountsApi.follow(account.username)
      onFollowed(account.id, account.username)
    } catch {
      setLocalFollowing(false)
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 16px',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <Link
        to={accountPath(account)}
        style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0, textDecoration: 'none' }}
      >
        <Avatar
          name={account.displayName || account.username}
          avatarUrl={account.avatarUrl}
          animalAvatar={account.animalAvatar}
          accountType={account.accountType}
          size="md"
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              color: 'var(--text-strong)',
              fontSize: 'var(--fs-body)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {account.displayName || account.username}
          </div>
          <div
            style={{
              fontFamily: 'var(--font-sans)',
              color: 'var(--text-muted)',
              fontSize: 'var(--fs-sm)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            @{account.username}
          </div>
          {account.badges.length > 0 && (
            <div style={{ marginTop: 2 }}>
              <BadgeList badges={account.badges} />
            </div>
          )}
        </div>
      </Link>
      <button
        type="button"
        onClick={localFollowing ? undefined : handleFollow}
        disabled={localFollowing}
        style={{
          flexShrink: 0,
          padding: '7px 16px',
          borderRadius: 'var(--radius-pill)',
          border: localFollowing ? '1.5px solid var(--border-strong)' : 'none',
          background: localFollowing ? 'transparent' : 'var(--brand)',
          color: localFollowing ? 'var(--text-muted)' : 'var(--on-brand)',
          fontFamily: 'var(--font-sans)',
          fontWeight: 600,
          fontSize: 'var(--fs-sm)',
          cursor: localFollowing ? 'default' : 'pointer',
        }}
      >
        {localFollowing ? 'Following' : 'Follow'}
      </button>
    </div>
  )
}

function SuggestedCard({ account, onFollowed }: { account: AccountWithStats; onFollowed: (id: string, username: string) => void }) {
  const { isAuthenticated } = useAuthStore()
  const navigate = useNavigate()
  const [localFollowing, setLocalFollowing] = useState(account.isFollowing ?? false)

  async function handleFollow() {
    if (!isAuthenticated) { navigate('/login'); return }
    setLocalFollowing(true)
    try {
      await accountsApi.follow(account.username)
      onFollowed(account.id, account.username)
    } catch {
      setLocalFollowing(false)
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        padding: '14px 12px',
        minWidth: 120,
        maxWidth: 140,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        flexShrink: 0,
        textAlign: 'center',
      }}
    >
      <Link to={accountPath(account)} style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
        <Avatar
          name={account.displayName || account.username}
          avatarUrl={account.avatarUrl}
          animalAvatar={account.animalAvatar}
          accountType={account.accountType}
          size="md"
        />
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontWeight: 700,
            color: 'var(--text-strong)',
            fontSize: 'var(--fs-sm)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 108,
            display: 'block',
          }}
        >
          {account.displayName || account.username}
        </span>
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            color: 'var(--text-muted)',
            fontSize: 'var(--fs-xs)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 108,
            display: 'block',
          }}
        >
          @{account.username}
        </span>
      </Link>
      <button
        type="button"
        onClick={localFollowing ? undefined : handleFollow}
        disabled={localFollowing}
        style={{
          padding: '5px 14px',
          borderRadius: 'var(--radius-pill)',
          border: localFollowing ? '1.5px solid var(--border-strong)' : 'none',
          background: localFollowing ? 'transparent' : 'var(--brand)',
          color: localFollowing ? 'var(--text-muted)' : 'var(--on-brand)',
          fontFamily: 'var(--font-sans)',
          fontWeight: 600,
          fontSize: 'var(--fs-xs)',
          cursor: localFollowing ? 'default' : 'pointer',
          marginTop: 2,
        }}
      >
        {localFollowing ? 'Following' : 'Follow'}
      </button>
    </div>
  )
}

function SkeletonCard() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        padding: '14px 12px',
        minWidth: 120,
        maxWidth: 140,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        flexShrink: 0,
      }}
    >
      <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--border)', opacity: 0.6 }} />
      <div style={{ width: 80, height: 10, borderRadius: 4, background: 'var(--border)', opacity: 0.6 }} />
      <div style={{ width: 60, height: 8, borderRadius: 4, background: 'var(--border)', opacity: 0.4 }} />
      <div style={{ width: 56, height: 26, borderRadius: 'var(--radius-pill)', background: 'var(--border)', opacity: 0.4, marginTop: 2 }} />
    </div>
  )
}

function SkeletonRow() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 16px',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--border)', flexShrink: 0, opacity: 0.6 }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ width: '40%', height: 10, borderRadius: 4, background: 'var(--border)', opacity: 0.6 }} />
        <div style={{ width: '25%', height: 8, borderRadius: 4, background: 'var(--border)', opacity: 0.4 }} />
      </div>
      <div style={{ width: 60, height: 30, borderRadius: 'var(--radius-pill)', background: 'var(--border)', flexShrink: 0, opacity: 0.4 }} />
    </div>
  )
}

const isMouseDevice = typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches

export function SuggestedAccounts({ mode, heading }: SuggestedAccountsProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { account: authAccount } = useAuthStore()
  const { suggestions, fetched, isLoading, fetchSuggestions, removeSuggestion } = useSuggestionsStore()

  function handleFollowed(id: string, username: string) {
    removeSuggestion(id)
    void queryClient.invalidateQueries({ queryKey: ['profile', authAccount?.username] })
    void queryClient.invalidateQueries({ queryKey: ['followers', username] })
    void queryClient.invalidateQueries({ queryKey: ['following', authAccount?.username] })
    void queryClient.invalidateQueries({ queryKey: ['feed'] })
  }

  useEffect(() => {
    void fetchSuggestions()
  }, [fetchSuggestions])

  // Loaded but empty — hide entirely
  if (fetched && suggestions.length === 0) return null

  const title = heading ?? (mode === 'strip' ? t('suggestions.people_you_might_know') : t('suggestions.suggested_accounts'))
  const showSkeleton = !fetched || isLoading

  if (mode === 'strip') {
    return (
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '12px 0 14px', width: '100%' }}>
        <p
          style={{
            margin: '0 0 10px',
            paddingInlineStart: 16,
            fontFamily: 'var(--font-sans)',
            fontWeight: 700,
            fontSize: 'var(--fs-body)',
            color: 'var(--text-strong)',
          }}
        >
          {title}
        </p>
        <div style={{ position: 'relative' }}>
          <div
            style={{
              display: 'flex',
              gap: 10,
              overflowX: 'auto',
              paddingInline: 16,
              paddingBottom: isMouseDevice ? 8 : 0,
              scrollbarWidth: isMouseDevice ? 'thin' : 'none',
              WebkitOverflowScrolling: 'touch',
            } as React.CSSProperties}
          >
            {showSkeleton
              ? [0, 1, 2, 3].map((i) => <SkeletonCard key={i} />)
              : suggestions.map((account) => (
                  <SuggestedCard key={account.id} account={account} onFollowed={handleFollowed} />
                ))}
          </div>
          {/* Right-fade gradient — signals there's more to scroll */}
          <div
            style={{
              position: 'absolute',
              insetInlineEnd: 0,
              top: 0,
              bottom: 0,
              width: 40,
              background: 'linear-gradient(to right, transparent, var(--surface))',
              pointerEvents: 'none',
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div style={{ background: 'var(--surface)' }}>
      <p
        style={{
          margin: '0',
          padding: '16px 16px 8px',
          fontFamily: 'var(--font-sans)',
          fontWeight: 700,
          fontSize: 'var(--fs-body)',
          color: 'var(--text-strong)',
        }}
      >
        {title}
      </p>
      {showSkeleton
        ? [0, 1, 2].map((i) => <SkeletonRow key={i} />)
        : suggestions.map((account) => (
            <SuggestedRow key={account.id} account={account} onFollowed={handleFollowed} />
          ))}
    </div>
  )
}
